/**
 * 阿里云盘视频 HLS 播放工具（单曲视频、图集内视频共用）
 */
import myRequest from '@/utils/myRequest';
import { createHlsCacheLoader } from '@/services/hlsCacheLoader.js';
import {
  getFullyCachedLevelKey,
  getPlaylist,
  normalizeSegmentUrl,
  rewritePlaylistWithAbsoluteUrls
} from '@/services/videoSegmentCache.js';

// 记录已加载的 hls.js 模块引用，供 createHlsJsConfig 取默认 loader 派生缓存 loader
let _hlsModuleRef = null;
export function _setHlsModuleRef(HlsRef) {
  _hlsModuleRef = HlsRef;
}

export const ALBUM_HLS_READY_PREFIX = 'album-hls-ready:';

/**
 * 图集 / 单曲视频共用：固定最高清晰度单路 m3u8，禁止 ABR 换档。
 */
export const ALBUM_HLS_QUALITY = {
  /** 始终用最高档 m3u8（direct 单路，不合成 master） */
  lockHighest: true,
  /** 禁止播放中切换其它清晰度 */
  disableAbr: true
};

const previewResponseCache = new Map();
const PREVIEW_CACHE_TTL_MS = 5 * 60 * 1000;
export const ALIYUN_HLS_QUALITY = {
  /** @deprecated 单曲已改用 resolveAlbumHlsSource 固定最高档；保留供其它场景参考 */
  preferHighestStart: true,
  /** iOS/Safari 原生 HLS 可选的最高高度，0 = 不限制（使用全部转码档） */
  iosMaxHeight: 0,
  /** hls.js：当前档带宽估算系数 */
  abrBandWidthFactor: 0.95,
  /** hls.js：升档所需带宽系数（越大越容易升档） */
  abrBandWidthUpFactor: 0.95
};

/** 是否为阿里云盘 CDN 直链（OSS 签名 URL） */
export function isAliyunCdnUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return url.includes('aliyundrive.cloud')
    || url.includes('alipan')
    || url.includes('x-oss-signature');
}

/** 图集媒体项是否为阿里云盘视频（走 HLS，不整文件预缓存） */
export function isAliyunAlbumVideo(item) {
  return item
    && item.mediaType === 1
    && item.storageType === 2
    && item.videoId != null;
}

export function isAlbumHlsReadyUrl(url) {
  return typeof url === 'string' && url.startsWith(ALBUM_HLS_READY_PREFIX);
}

export function buildAlbumHlsReadyUrl(videoId) {
  return `${ALBUM_HLS_READY_PREFIX}${videoId}`;
}

/**
 * 图集媒体项是否已有可解析的播放地址（含 HLS 就绪标记，避免被批量 URL 更新误判为缺失）
 * 阿里云 CDN 直链会过期（403），图集图片只有 blob / HLS 标记才算就绪
 */
export function isResolvedAlbumMediaUrl(url, item) {
  if (url) {
    if (url.startsWith('blob:')) return true;
    if (isAlbumHlsReadyUrl(url)) return true;
  }
  if (isAliyunAlbumVideo(item)) return true;
  return false;
}

/** 为图集媒体项标注 playMethod（加载列表后调用） */
export function annotateAlbumMediaItems(items) {
  if (!Array.isArray(items)) return items;
  return items.map((item) => {
    if (isAliyunAlbumVideo(item)) {
      return { ...item, playMethod: 'hls' };
    }
    return item;
  });
}

/**
 * 播放顺序：图片优先、视频置后（同类型内仍按 sortOrder）。
 * 视频 HLS 冷启动慢，先给用户看图，视频挪到后面再播。
 */
export function orderAlbumMediaImagesFirst(items) {
  if (!Array.isArray(items) || items.length <= 1) return items;
  return [...items].sort((a, b) => {
    const aVideo = Number(a?.mediaType) === 1 ? 1 : 0;
    const bVideo = Number(b?.mediaType) === 1 ? 1 : 0;
    if (aVideo !== bVideo) return aVideo - bVideo;
    return (Number(a?.sortOrder) || 0) - (Number(b?.sortOrder) || 0);
  });
}

/**
 * 图集列表统一整理：先按「图优先 / 视频后」排序，再标注 HLS playMethod。
 */
export function prepareAlbumMediaItemsForPlayback(items) {
  return annotateAlbumMediaItems(orderAlbumMediaImagesFirst(items));
}

/**
 * 单曲视频：GET /api/music/aliyun-video-preview/{musicId}
 */
export async function fetchMusicAliyunPreview(musicId) {
  try {
    const response = await myRequest.get(`/api/music/aliyun-video-preview/${musicId}`);
    return response?.playMethod ? response : null;
  } catch {
    return null;
  }
}

/**
 * 图集/独立视频：GET /api/video/aliyun-video-preview/{videoId}?musicId=
 */
export async function fetchVideoAliyunPreview(videoId, musicId) {
  try {
    const params = musicId != null ? { musicId } : undefined;
    const response = await myRequest.get(`/api/video/aliyun-video-preview/${videoId}`, { params });
    return response?.playMethod ? response : null;
  } catch {
    return null;
  }
}

/** 带内存 TTL 的 preview 缓存，减少图集切换时重复请求后端 */
export async function fetchVideoAliyunPreviewCached(videoId, musicId) {
  const cacheKey = `${videoId}:${musicId ?? ''}`;
  const hit = previewResponseCache.get(cacheKey);
  if (hit && Date.now() - hit.ts < PREVIEW_CACHE_TTL_MS) {
    return hit.preview;
  }
  const preview = await fetchVideoAliyunPreview(videoId, musicId);
  if (preview) {
    previewResponseCache.set(cacheKey, { preview, ts: Date.now() });
  }
  return preview;
}

export function clearVideoAliyunPreviewCache(videoId) {
  if (videoId == null) {
    previewResponseCache.clear();
    return;
  }
  for (const key of previewResponseCache.keys()) {
    if (key.startsWith(`${videoId}:`)) {
      previewResponseCache.delete(key);
    }
  }
}

const BANDWIDTH_BY_TEMPLATE = {
  LD: 600000,
  SD: 1300000,
  HD: 2600000,
  FHD: 5200000,
  QHD: 9000000,
  '264_360p': 600000,
  '264_480p': 1300000,
  '264_720p': 2600000,
  '264_1080p': 5200000,
  '264_1440p': 9000000,
  '264_2160p': 16000000
};

/** 估算单路流带宽（bps），供 master playlist 与 hls.js ABR 使用 */
export function estimateStreamBandwidth(stream) {
  if (!stream) return 1500000;
  const tid = stream.templateId;
  if (tid && BANDWIDTH_BY_TEMPLATE[tid]) {
    return BANDWIDTH_BY_TEMPLATE[tid];
  }
  const match = String(tid || '').match(/(\d+)p/i);
  if (match) {
    const h = parseInt(match[1], 10);
    if (h <= 360) return 600000;
    if (h <= 480) return 1300000;
    if (h <= 720) return 2600000;
    if (h <= 1080) return 5200000;
    return 9000000;
  }
  if (stream.width && stream.height) {
    return Math.round(stream.width * stream.height * 4);
  }
  return 1500000;
}

/** 归一化后端/阿里云字段（camelCase / snake_case） */
export function normalizeHlsStreams(streams) {
  if (!Array.isArray(streams)) return [];
  return streams
    .map((s) => ({
      templateId: String(s?.templateId || s?.template_id || ''),
      url: typeof s?.url === 'string' ? s.url.trim() : '',
      width: Number(s?.width ?? s?.template_width) || 0,
      height: Number(s?.height ?? s?.template_height) || 0
    }))
    .filter((s) => /^https?:\/\//i.test(s.url));
}

/** 同一 m3u8 URL 只保留最高分辨率一档（阿里云未完成转码时多档可能共用 URL） */
export function dedupeStreamsByUrl(streams) {
  const map = new Map();
  for (const s of streams) {
    const prev = map.get(s.url);
    if (!prev || (s.height || 0) > (prev.height || 0)) {
      map.set(s.url, s);
    }
  }
  return [...map.values()].sort((a, b) => (a.height || 0) - (b.height || 0));
}

/** 按高度升序排列 */
export function sortStreamsByHeight(streams) {
  return dedupeStreamsByUrl(normalizeHlsStreams(streams));
}

/** 过滤/排序可用 HLS 流；iOS 原生回退时可按 ALIYUN_HLS_QUALITY.iosMaxHeight 封顶 */
export function prepareHlsStreams(streams) {
  const sorted = sortStreamsByHeight(streams);
  if (!sorted.length) return sorted;
  const rawCount = Array.isArray(streams) ? streams.length : 0;
  if (rawCount > sorted.length) {
    console.log(
      '[HLS] 有效清晰度流',
      sorted.length,
      '/',
      rawCount,
      '→',
      sorted.map((s) => `${s.templateId || '?'}(${s.width}x${s.height})`).join(', ')
    );
  }
  const maxH = ALIYUN_HLS_QUALITY.iosMaxHeight;
  if (!maxH) return sorted;
  const isIOS = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent);
  if (!isIOS) return sorted;
  const capped = sorted.filter((s) => !s.height || s.height <= maxH);
  return capped.length > 0 ? capped : sorted;
}

/**
 * 起播档位索引：默认最高档（见 ALIYUN_HLS_QUALITY）；单档则 0。
 */
export function computeStartLevelIndex(sortedStreams, { preferLowestStart = false } = {}) {
  if (!sortedStreams?.length) return 0;
  if (sortedStreams.length === 1) return 0;
  const preferHighest = ALIYUN_HLS_QUALITY.preferHighestStart && !preferLowestStart;
  return preferHighest ? sortedStreams.length - 1 : 0;
}

/** 选取最高分辨率流的 URL（已按高度升序排列） */
export function pickHighestStreamUrl(streams) {
  if (!streams?.length) return null;
  return streams[streams.length - 1].url;
}

export function estimateHeightFromTemplate(templateId) {
  const match = String(templateId || '').match(/(\d+)p/i);
  return match ? parseInt(match[1], 10) : 0;
}

/** 选取最接近目标高度的流 URL（默认 480p 中档） */
export function pickStreamUrl(streams, preferHeight = 480) {
  if (!streams?.length) return null;
  let best = streams[0];
  let bestDist = Infinity;
  for (const s of streams) {
    const h = s.height || estimateHeightFromTemplate(s.templateId) || preferHeight;
    const dist = Math.abs(h - preferHeight);
    if (dist < bestDist) {
      bestDist = dist;
      best = s;
    }
  }
  return best.url;
}

/**
 * 决定 hls.js 加载方式：多档且 URL 不同 → 合成 master；否则直链 m3u8（更快，避免 levelLoadTimeOut）
 * @param {{ preferLowestStart?: boolean }} options - 图集首项视频等场景优先最低档起播
 */
export function resolveHlsSource(preparedStreams, { preferLowestStart = false } = {}) {
  const streams = sortStreamsByHeight(preparedStreams);
  if (!streams.length) {
    throw new Error('无有效 HLS 流');
  }
  const distinctUrls = new Set(streams.map((s) => s.url));
  if (distinctUrls.size <= 1) {
    return {
      mode: 'direct',
      url: preferLowestStart ? streams[0].url : pickHighestStreamUrl(streams),
      streams,
      startLevelIndex: 0,
      masterBlobUrl: null
    };
  }
  const startLevelIndex = computeStartLevelIndex(streams, { preferLowestStart });
  return {
    mode: 'master',
    url: null,
    streams,
    startLevelIndex,
    masterBlobUrl: buildHlsMasterPlaylist(streams)
  };
}

/**
 * 图集专用：只取最高清晰度单路 m3u8（direct），避免 master + ABR 换档。
 * 单曲视频（VideoController）同样使用此策略。
 */
export function resolveAlbumHlsSource(preparedStreams) {
  const streams = sortStreamsByHeight(preparedStreams);
  if (!streams.length) {
    throw new Error('无有效 HLS 流');
  }
  const highest = streams[streams.length - 1];
  const levelKey = normalizeSegmentUrl(highest.url);
  return {
    mode: 'direct',
    url: highest.url,
    streams: [highest],
    startLevelIndex: 0,
    masterBlobUrl: null,
    levelKey
  };
}

/** 图集视频分片已完整缓存时，用本地 m3u8 起播（不再请求 preview / 其它清晰度） */
export async function resolveCachedAlbumHlsSource(videoId) {
  const levelKey = await getFullyCachedLevelKey(videoId);
  if (!levelKey) return null;
  const content = await getPlaylist(videoId, levelKey);
  if (!content) return null;
  const rewritten = rewritePlaylistWithAbsoluteUrls(content, levelKey);
  const blob = new Blob([rewritten], { type: 'application/vnd.apple.mpegurl' });
  const blobUrl = URL.createObjectURL(blob);
  return {
    mode: 'cached',
    url: blobUrl,
    masterBlobUrl: blobUrl,
    streams: [],
    startLevelIndex: 0,
    levelKey,
    fromCache: true
  };
}

/** 由多清晰度 m3u8 构建 master playlist Blob URL */
export function buildHlsMasterPlaylist(streams) {
  const sorted = Array.isArray(streams) && streams[0]?.url
    ? [...streams].sort((a, b) => (a.height || 0) - (b.height || 0))
    : sortStreamsByHeight(streams);
  const lines = ['#EXTM3U', '#EXT-X-VERSION:3'];
  for (const s of sorted) {
    const bw = estimateStreamBandwidth(s);
    const res = (s.width && s.height) ? `,RESOLUTION=${s.width}x${s.height}` : '';
    lines.push(`#EXT-X-STREAM-INF:BANDWIDTH=${bw}${res}`);
    lines.push(s.url);
  }
  const text = lines.join('\n');
  const blob = new Blob([text], { type: 'application/vnd.apple.mpegurl' });
  return URL.createObjectURL(blob);
}

/** hls.js 配置：最高档起播；图集模式 lockLevel 禁止 ABR */
export function createHlsJsConfig(startLevelIndex, { prefetch = false, cacheCtx = null, lockLevel = false } = {}) {
  const config = {
    enableWorker: true,
    lowLatencyMode: false,
    startLevel: lockLevel ? 0 : startLevelIndex,
    // 不因歌词区 video 元素 DOM 尺寸偏小而锁死低码率
    capLevelToPlayerSize: false,
    maxBufferLength: 30,
    maxMaxBufferLength: 60,
    backBufferLength: 10,
    abrBandWidthFactor: ALIYUN_HLS_QUALITY.abrBandWidthFactor,
    abrBandWidthUpFactor: ALIYUN_HLS_QUALITY.abrBandWidthUpFactor,
    abrMaxWithRealBitrate: true,
    startFragPrefetch: true,
    manifestLoadingTimeOut: 12000,
    levelLoadingTimeOut: 12000,
    fragLoadingTimeOut: 20000,
    // 阿里云 CDN 签名链路与 <video referrerpolicy="no-referrer"> 保持一致
    fetchSetup: (context, initParams) => {
      initParams.referrerPolicy = 'no-referrer';
      initParams.credentials = 'omit';
      initParams.mode = 'cors';
      return initParams;
    },
    xhrSetup: (xhr) => {
      xhr.withCredentials = false;
    }
  };
  if (lockLevel) {
    config.minAutoLevel = 0;
    config.maxAutoLevel = 0;
    config.autoLevelCapping = -1;
  }
  if (prefetch) {
    // Live 图短视频：只缓冲前几秒，降低预加载流量
    config.maxBufferLength = 8;
    config.maxMaxBufferLength = 12;
    config.backBufferLength = 0;
  }
  // 注入阿里云盘视频分片缓存 loader（仅在提供 videoId 时启用）
  if (cacheCtx && cacheCtx.videoId != null) {
    console.log(`🎞️ [aliyunHlsPlayback] 尝试安装分片缓存 loader, videoId=${cacheCtx.videoId}`);
    try {
      installHlsCacheLoader(config, cacheCtx);
      console.log('🎞️ [aliyunHlsPlayback] 分片缓存 loader 安装成功');
    } catch (e) {
      console.warn('[aliyunHlsPlayback] 安装分片缓存 loader 失败，回退默认 loader:', e);
    }
  } else {
    console.log('🎞️ [aliyunHlsPlayback] 未提供 cacheCtx 或 videoId，使用默认 loader');
  }
  return config;
}

/**
 * 在 hls.js config 上安装分片缓存 loader。
 * 需要在 new Hls(config) 前调用；BaseLoader 取自 Hls.DefaultConfig.loader。
 */
function installHlsCacheLoader(config, cacheCtx) {
  // 延迟引用，避免与按需 import('hls.js') 的加载时序冲突
  // 通过全局缓存的 Hls（attachHlsToVideo 中已 import）拿默认 loader
  console.log('🎞️ [installHlsCacheLoader] _hlsModuleRef 状态:', _hlsModuleRef ? 'Available' : 'NULL');
  const HlsRef = _hlsModuleRef;
  if (!HlsRef || !HlsRef.DefaultConfig || !HlsRef.DefaultConfig.loader) {
    console.warn('🎞️ [installHlsCacheLoader] 无法获取 Hls.DefaultConfig.loader，跳过缓存 loader 安装');
    return; // 拿不到默认 loader 时不启用缓存，保证不破坏播放
  }
  const BaseLoader = HlsRef.DefaultConfig.loader;
  console.log('🎞️ [installHlsCacheLoader] 创建缓存 loader，videoId:', cacheCtx.videoId);
  config.loader = createHlsCacheLoader(BaseLoader, cacheCtx);
  console.log('🎞️ [installHlsCacheLoader] config.loader 已设置为缓存 loader');
}

const HLS_LOAD_TIMEOUT_DETAILS = new Set([
  'levelLoadTimeOut',
  'manifestLoadTimeOut',
  'fragLoadTimeOut'
]);

/** 绑定 ABR 调试日志（起播档位、升/降档、缓冲卡住） */
export function bindHlsAbrEvents(hls, Hls, logPrefix = '[HLS]') {
  if (!hls || !Hls) return;
  hls.on(Hls.Events.MANIFEST_PARSED, () => {
    const idx = hls.startLevel >= 0 ? hls.startLevel : hls.currentLevel;
    const level = hls.levels?.[idx];
    const w = level?.width || '?';
    const h = level?.height || '?';
    console.log(`${logPrefix} 起播档位 index=${idx}, ${w}x${h}`);
  });
  hls.on(Hls.Events.LEVEL_SWITCHED, (_event, data) => {
    const level = hls.levels?.[data.level];
    const w = level?.width || '?';
    const h = level?.height || '?';
    console.log(`${logPrefix} 自适应切档 → index=${data.level}, ${w}x${h}`);
  });
}

/**
 * level/manifest 超时后回退直链 m3u8，避免合成 master 卡十几秒
 * @returns {{ directFallbackUsed: boolean }} 可变状态对象
 */
export function bindAliyunHlsNetworkFallback(hls, Hls, sourceInfo, logPrefix, onNonFatal) {
  const state = { directFallbackUsed: false };
  if (!hls || !Hls || !sourceInfo) return state;

  hls.on(Hls.Events.ERROR, (_event, data) => {
    if (!data) return;
    if (
      !state.directFallbackUsed
      && sourceInfo.mode === 'master'
      && HLS_LOAD_TIMEOUT_DETAILS.has(data.details)
    ) {
      const fallbackUrl = pickHighestStreamUrl(sourceInfo.streams);
      if (fallbackUrl) {
        state.directFallbackUsed = true;
        console.warn(`${logPrefix} ${data.details}，回退直链 m3u8 起播`);
        hls.loadSource(fallbackUrl);
        return;
      }
    }
    if (onNonFatal) onNonFatal(data);
  });
  return state;
}

export function createHlsPlayerState() {
  return { hls: null, masterBlobUrl: null, loadGeneration: 0 };
}

/** 销毁 hls.js 实例并释放 master playlist Blob */
export function destroyHlsPlayerState(state, videoEl) {
  if (!state) return;
  if (state.hls) {
    try { state.hls.destroy(); } catch (_) { /* ignore */ }
    state.hls = null;
  }
  if (state.masterBlobUrl) {
    try {
      if (videoEl && videoEl.src === state.masterBlobUrl) {
        videoEl.removeAttribute('src');
        videoEl.load();
      }
    } catch (_) { /* ignore */ }
    try { URL.revokeObjectURL(state.masterBlobUrl); } catch (_) { /* ignore */ }
    state.masterBlobUrl = null;
  }
}

/**
 * 在指定 video 元素上挂载 HLS（Safari 原生 / hls.js）
 * @returns {Promise<void>} canplay 后 resolve
 */
export async function attachHlsToVideo(state, videoEl, streams, {
  startTime = 0,
  deferAutoPlay = true,
  isStale = () => false,
  prefetch = false,
  preferLowestQuality = false,
  albumMode = false,
  useCachedSource = null,
  cacheCtx = null
} = {}) {
  if (!videoEl) {
    throw new Error('HLS 参数无效');
  }
  if (!useCachedSource && !streams?.length) {
    throw new Error('HLS 参数无效');
  }

  state.loadGeneration += 1;
  const generation = state.loadGeneration;
  destroyHlsPlayerState(state, videoEl);

  if (isStale()) {
    throw new Error('HLS 加载已取消');
  }

  videoEl.setAttribute('referrerpolicy', 'no-referrer');

  let sourceInfo;
  if (useCachedSource) {
    sourceInfo = {
      mode: 'cached',
      url: useCachedSource.url,
      streams: [],
      startLevelIndex: 0,
      masterBlobUrl: useCachedSource.masterBlobUrl,
      levelKey: useCachedSource.levelKey,
      fromCache: true
    };
    state.masterBlobUrl = useCachedSource.masterBlobUrl;
  } else {
    const preparedStreams = prepareHlsStreams(streams);
    sourceInfo = albumMode
      ? resolveAlbumHlsSource(preparedStreams)
      : resolveHlsSource(preparedStreams, { preferLowestStart: preferLowestQuality });
    state.masterBlobUrl = sourceInfo.masterBlobUrl;
  }

  const loadUrl = (sourceInfo.mode === 'direct' || sourceInfo.mode === 'cached')
    ? sourceInfo.url
    : sourceInfo.masterBlobUrl;
  console.log(
    '[aliyunHlsPlayback] 加载方式:',
    sourceInfo.mode,
    '档位数:',
    sourceInfo.streams?.length ?? 0,
    '起播 index:',
    sourceInfo.startLevelIndex,
    albumMode ? '(图集最高档锁定)' : (preferLowestQuality ? '(最低画质)' : ''),
    sourceInfo.fromCache ? '(本地缓存)' : ''
  );

  const waitReady = () => new Promise((resolve, reject) => {
    let settled = false;
    let pollTimer = null;
    let timeoutTimer = null;

    const cleanup = () => {
      if (settled) return;
      settled = true;
      videoEl.removeEventListener('canplay', onCanPlay);
      videoEl.removeEventListener('error', onError);
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
      if (timeoutTimer) {
        clearTimeout(timeoutTimer);
        timeoutTimer = null;
      }
    };

    const fail = (error) => {
      cleanup();
      reject(error);
    };

    const onCanPlay = async () => {
      if (isStale() || state.loadGeneration !== generation) {
        fail(new Error('HLS 加载已取消'));
        return;
      }
      try {
        if (!deferAutoPlay) {
          await videoEl.play();
        }
        cleanup();
        resolve();
      } catch (e) {
        fail(e);
      }
    };

    const onError = () => {
      fail(new Error('HLS 视频加载失败'));
    };

    // 分片缓存 loader abort / 切项后 canplay 可能永远不来，必须主动侦测 stale
    pollTimer = setInterval(() => {
      if (isStale() || state.loadGeneration !== generation) {
        fail(new Error('HLS 加载已取消'));
      }
    }, 80);

    timeoutTimer = setTimeout(() => {
      fail(new Error('HLS 加载超时'));
    }, albumMode ? 12000 : 20000);

    videoEl.addEventListener('canplay', onCanPlay);
    videoEl.addEventListener('error', onError);
    if (videoEl.readyState >= 3 && !isStale()) {
      onCanPlay();
    }
  });

  const Hls = (await import('hls.js')).default;
  if (isStale() || state.loadGeneration !== generation) {
    throw new Error('HLS 加载已取消');
  }
  // 记录 Hls 模块引用，供 createHlsJsConfig 取默认 loader 派生缓存 loader
  _setHlsModuleRef(Hls);

  // 优先 hls.js：统一 ABR（最高档起播、卡顿降档、带宽充足升档）
  if (Hls.isSupported()) {
    const lockLevel = albumMode || !!useCachedSource;
    const mergedCacheCtx = cacheCtx && cacheCtx.videoId != null
      ? {
        ...cacheCtx,
        levelKey: cacheCtx.levelKey || useCachedSource?.levelKey || sourceInfo.levelKey || null
      }
      : null;
    const hls = new Hls(createHlsJsConfig(sourceInfo.startLevelIndex, {
      prefetch,
      cacheCtx: mergedCacheCtx,
      lockLevel
    }));
    state.hls = hls;
    hls.attachMedia(videoEl);
    hls.loadSource(loadUrl);
    if (!lockLevel) {
      bindHlsAbrEvents(hls, Hls, '[aliyunHlsPlayback]');
    } else {
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        hls.loadLevel = 0;
        hls.currentLevel = 0;
        const level = hls.levels?.[0];
        console.log(
          `[aliyunHlsPlayback] 图集锁定档位 index=0, ${level?.width || '?'}x${level?.height || '?'}`
        );
      });
    }
    bindAliyunHlsNetworkFallback(hls, Hls, sourceInfo, '[aliyunHlsPlayback]', (data) => {
      if (data?.fatal) {
        console.error('[aliyunHlsPlayback] HLS 致命错误:', data.type, data.details);
      } else if (data.details === 'bufferStalledError') {
        console.warn('[aliyunHlsPlayback] 缓冲卡住，ABR 将尝试降档');
      }
    });
    if (startTime > 0) videoEl.currentTime = startTime;
    return waitReady();
  }

  // 回退：Safari/iOS 原生 HLS（档位见 ALIYUN_HLS_QUALITY.iosMaxHeight）
  const canNative = videoEl.canPlayType('application/vnd.apple.mpegurl');
  if (canNative) {
    console.log('[aliyunHlsPlayback] 使用原生 HLS（iOS/Safari），档位:', sourceInfo.streams.length);
    videoEl.src = loadUrl;
    videoEl.load();
    if (startTime > 0) videoEl.currentTime = startTime;
    return waitReady();
  }

  destroyHlsPlayerState(state, videoEl);
  throw new Error('当前浏览器不支持 HLS');
}

/** 将预缓冲 slot 上的 hls.js 实例转移到正式播放的 video 元素（不销毁 hls） */
export function transferHlsToVideo(fromState, fromVideoEl, toState, toVideoEl) {
  if (!fromState?.hls || !toVideoEl) return false;
  try {
    fromState.hls.detachMedia();
  } catch (_) { /* ignore */ }
  fromState.hls.attachMedia(toVideoEl);
  toState.hls = fromState.hls;
  toState.masterBlobUrl = fromState.masterBlobUrl;
  toState.loadGeneration = fromState.loadGeneration;
  fromState.hls = null;
  fromState.masterBlobUrl = null;
  if (fromVideoEl) {
    try {
      fromVideoEl.removeAttribute('src');
      fromVideoEl.load();
    } catch (_) { /* ignore */ }
  }
  return true;
}
